// CARREIRA: o estado do jogo, e as unicas funcoes que podem altera-lo.
//
// Regra de ouro deste arquivo: a UI nunca escreve no estado direto. Toda
// mudanca passa por uma acao daqui, que devolve o estado novo e a lista de
// avisos para a tela mostrar. Aviso que ninguem consegue ler e bug -- no
// projeto anterior, mensagem importante so aparecia numa tela especifica.
//
// ORCAMENTO DE SAVE (medido, nao chutado):
//   - elencos dos 30 times ....... ~190 KB
//   - resultados (so o placar) ...  ~60 KB
//   - totais da temporada ........ ~110 KB
//   - ultimas 6 sumulas completas .. ~65 KB
// Guardar a sumula de todos os 1230 jogos daria 7,5 MB e nao caberia. Por isso
// o historico e AGREGADO, e so as ultimas partidas ficam inteiras.

import { makeRng } from './rng.js';
import { getLeagueConfig, LEAGUE_LIST } from '../data/leagues.js';
import { buildRoster as buildRealRoster } from '../data/real-rosters.js';
import { makePlayer, POSITIONS, assignTargetMinutes, makeRoster } from './players.js';
import { simulateGame, createMatch, matchResult } from './game.js';
import { enrichPlayer, payrollOf, luxuryTaxOf, wageOf, econOf, MONEY } from './economy.js';
import {
  generateAiOffers, quote, evaluateBid, blockers, aiAcceptsCounter, releaseCost, aiWants, aiMaxFee,
} from './market.js';
import { buildDraft, currentPick, aiChoice } from './draft.js';
import { computeAwards } from './awards.js';
import { makeSponsorOffers, pagamentoDoPatrocinio } from './sponsors.js';
import { publicarManchete } from './imprensa.js';
import { assignTargetMinutes as applyMinutes, suggestedMinutes } from './players.js';
import { DEFAULT_TACTICS } from './tuning.js';
import {
  JOB, seasonTarget, targetLabel, boardMood, moodLabel, shouldFireMidSeason,
  shouldFireEndOfSeason, updateReputation, reputationLabel, jobOffers,
} from './jobs.js';
import {
  buildSchedule, emptyStandings, applyResult, buildPlayoffs, advanceBracket,
  seriesHomeTeam, accumulateGame, sortedStandings, seriesWinsNeeded,
} from './season.js';

export const SAVE_KEY = 'basquete82.carreira.v1';
const KEEP_BOXSCORES = 6;

/** Configuracao da liga desta carreira. Formato, economia e nomes vem daqui. */
export const configOf = (state) => getLeagueConfig(state.leagueId);
export const economyOf = (state) => econOf(configOf(state));
export const regularRoundsOf = (state) => configOf(state).regularRounds;
export const deadlineOf = (state) => configOf(state).tradeDeadlineRound;
export { LEAGUE_LIST };

// ---------------------------------------------------------------- criacao

/**
 * Liga nova a partir do catalogo: times reais, elencos ficticios por semente.
 * Gerar de novo sorteia outros elencos para os mesmos clubes.
 */
export function newLeague(leagueId = 'nba', seed = String(Date.now())) {
  const config = getLeagueConfig(leagueId);
  const econ = econOf(config);
  const takenNames = new Set();
  const size = Math.max(econ.minRoster + 1, econ.maxRoster - 2);
  const teams = config.teams.map((entry, i) => {
    const id = `${config.id}${String(i + 1).padStart(2, '0')}`;
    // Elenco real primeiro; o gerador completa o que faltar.
    const roster = buildRealRoster({
      leagueId: config.id,
      teamName: entry.name,
      size,
      season: 1,
      strength: entry.strength,
      level: config.level,
      spread: config.talentSpread ?? 1,
      nationality: config.id === 'nbb' ? 'BR' : 'US',
      takenNames,
      fallback: makeRoster,
    });
    for (const player of roster) enrichPlayer(player, { seed, scale: econ.moneyScale });
    const payroll = payrollOf(roster);
    const rng = makeRng(`caixa|${seed}|${id}`);
    const cash = econ.startingCash - payroll * 0.5
      + rng.range(-econ.startingCash * 0.1, econ.startingCash * 0.22);
    return {
      id,
      name: entry.name,
      conference: entry.conference,
      strength: entry.strength,
      tactics: { pace: 0, threeBias: 0 },
      roster,
      money: Math.max(econ.startingCash * 0.05, Math.round(cash * 10) / 10),
    };
  });
  return { seed: String(seed), leagueId: config.id, season: 1, teams };
}

/** A força do elenco perto do resto da liga, de 0 a 1. */
function forcaRelativa(teams, clubId) {
  const media8 = (t) => {
    const top = [...t.roster].sort((a, b) => b.overall - a.overall).slice(0, 8);
    return top.reduce((acc, p) => acc + p.overall, 0) / Math.max(1, top.length);
  };
  const todas = teams.map(media8);
  const minha = media8(teams.find((t) => t.id === clubId) || teams[0]);
  const pior = Math.min(...todas);
  const melhor = Math.max(...todas);
  return melhor > pior ? (minha - pior) / (melhor - pior) : 0.5;
}

export function createCareer({ league, clubId, coachName = 'Tecnico' }) {
  const seed = league.seed;
  const config = getLeagueConfig(league.leagueId);
  return {
    version: 1,
    seed,
    leagueId: config.id,
    coachName,
    createdAt: new Date().toISOString(),
    clubId,
    season: 1,
    round: 1,
    phase: 'regular',
    teams: league.teams,
    schedule: buildSchedule(league.teams, seed, config.regularRounds),
    standings: emptyStandings(league.teams),
    seasonStats: {},
    results: [],
    boxscores: [],
    bracket: null,
    // Patrocínio: o contrato ativo e as propostas na mesa.
    sponsor: null,
    /* ⚠️ As propostas nascem JÁ na temporada 1.
       Antes só apareciam em startNextSeason, ou seja, na T2: quem jogava o
       primeiro ano inteiro nunca via patrocínio nenhum e concluía, com razão,
       que a função não existia. Um clube tem patrocinador desde o primeiro
       dia. */
    sponsorOffers: makeSponsorOffers({
      econ: econOf(config),
      seed,
      season: 1,
      // Sem histórico ainda: a força vem do elenco, e o aproveitamento é
      // neutro. Ninguém tem campanha passada na primeira temporada.
      forca: forcaRelativa(league.teams, clubId),
      aproveitamento: 0.5,
    }),
    listed: [],
    offers: [],
    reputation: JOB.startingReputation,
    unemployed: false,
    jobOffers: null,
    awards: null,
    news: [{ round: 0, text: `Voce assumiu o ${teamName(league.teams, clubId)}. Boa temporada.`, kind: 'info' }],
    history: [],
  };
}

const teamName = (teams, id) => teams.find((t) => t.id === id)?.name || id;
export const userTeam = (state) => state.teams.find((t) => t.id === state.clubId);
export const teamById = (state, id) => state.teams.find((t) => t.id === id);

// ------------------------------------------------------------ avancar jogo

/** Jogo do usuario na rodada atual da temporada regular. */
export function userFixture(state) {
  const round = state.schedule[state.round - 1];
  if (!round) return null;
  return round.find((g) => g.home === state.clubId || g.away === state.clubId) || null;
}

/**
 * Avanca UMA rodada (ou um jogo de cada serie, nos playoffs).
 * Devolve { state, events } -- eventos sao o que a tela deve contar ao usuario.
 */
export function advanceRound(state, opts = {}) {
  if (state.phase === 'regular') return advanceRegularRound(state, opts);
  if (state.phase === 'playoffs') return advancePlayoffRound(state, opts);
  return { state, events: [{ kind: 'info', text: 'A temporada acabou. Comece a proxima.' }] };
}

function advanceRegularRound(state, opts = {}) {
  const events = [];
  const config = configOf(state);
  const econ = economyOf(state);
  const fixtures = state.schedule[state.round - 1] || [];
  const rng = makeRng(`rodada|${state.seed}|${state.season}|${state.round}`);

  for (const fixture of fixtures) {
    const home = teamById(state, fixture.home);
    const away = teamById(state, fixture.away);
    const isUser = home.id === state.clubId || away.id === state.clubId;
    // Se a partida do usuario ja foi jogada na tela, e ELA que vale -- com as
    // substituicoes que ele fez. Simular de novo aqui jogaria fora as decisoes
    // dele e mostraria um placar diferente do que ele acabou de ver.
    const result = (isUser && opts.userResult) ? opts.userResult : simulateGame({
      home,
      away,
      seed: `${state.seed}|s${state.season}|r${state.round}|${home.id}`,
      playByPlay: isUser,
      rules: config.rules,
    });
    applyResult(state.standings, result);
    accumulateGame(state.seasonStats, result);
    state.results.push({
      season: state.season,
      round: state.round,
      home: home.id,
      away: away.id,
      hs: result.home.score,
      as: result.away.score,
    });
    if (isUser) {
      state.boxscores.unshift(compactBox(result, state.round, state.season));
      state.boxscores.length = Math.min(state.boxscores.length, KEEP_BOXSCORES);
      const us = home.id === state.clubId ? result.home : result.away;
      const them = home.id === state.clubId ? result.away : result.home;
      events.push({
        kind: us.score > them.score ? 'win' : 'loss',
        text: `${us.score > them.score ? 'Vitoria' : 'Derrota'} ${us.score} a ${them.score} contra o ${them.name}.`,
        result,
      });
    }
  }

  // Mercado: propostas chegam ate a data limite de trocas.
  if (!state.unemployed && state.round <= config.tradeDeadlineRound) {
    const fresh = generateAiOffers({
      league: { teams: state.teams },
      userTeam: userTeam(state),
      listedIds: state.listed,
      standings: state.standings,
      round: state.round,
      season: state.season,
      rng,
      econ,
      deadlineRound: config.tradeDeadlineRound,
    });
    for (const offer of fresh) {
      state.offers.push(offer);
      events.push({
        kind: 'offer',
        text: `${offer.buyerName} ofereceu ${offer.fee.toFixed(1)} mi por ${offer.playerName}.`,
      });
    }
  } else if (state.round === config.tradeDeadlineRound + 1) {
    events.push({ kind: 'info', text: 'A data limite de trocas passou. O mercado reabre na entressafra.' });
  }

  aiTransferRound(state, rng, events, econ);

  state.round += 1;

  // A diretoria olha a tabela depois da rodada.
  if (!state.unemployed) {
    const target = seasonTarget(userTeam(state), state.teams);
    if (shouldFireMidSeason({
      record: state.standings[state.clubId], target,
      round: state.round, seed: state.seed, season: state.season,
    })) {
      events.push(fireUser(state, `A meta era ${Math.round(target * 100)}% de vitorias.`));
    }
  }

  /* A imprensa fala DEPOIS de tudo que aconteceu na rodada -- inclusive da
     demissao, se ela veio. Uma manchete escrita antes contaria a rodada pela
     metade. */
  publicarManchete(state);

  // Propostas vencidas caem sozinhas -- DEPOIS de avancar a rodada. Filtrar
  // antes deixava na tela uma proposta marcada como "vence na rodada 25"
  // enquanto o placar ja mostrava a rodada 26.
  const before = state.offers.length;
  state.offers = state.offers.filter((o) => o.expiresRound >= state.round);
  if (state.offers.length < before) {
    events.push({ kind: 'info', text: `${before - state.offers.length} proposta(s) expiraram.` });
  }
  if (state.round > config.regularRounds) {
    state.phase = 'playoffs';
    state.bracket = buildPlayoffs(state.standings, config);
    const row = state.standings[state.clubId];
    const classified = state.bracket.series.some((s) => s.teams.includes(state.clubId));
    events.push({
      kind: classified ? 'win' : 'loss',
      text: classified
        ? `Temporada regular encerrada: ${row.wins}-${row.losses}. Voce esta nos playoffs.`
        : `Temporada regular encerrada: ${row.wins}-${row.losses}. Sem playoffs neste ano.`,
    });
  }
  return { state, events };
}

function advancePlayoffRound(state, opts = {}) {
  const events = [];
  const config = configOf(state);
  const active = state.bracket.series.filter((s) => s.round === state.bracket.round && !s.done);
  const needed = seriesWinsNeeded(config, state.bracket.round);

  for (const series of active) {
    const gameNumber = series.games.length + 1;
    const homeId = seriesHomeTeam(series, gameNumber, needed);
    const awayId = series.teams.find((id) => id !== homeId);
    const home = teamById(state, homeId);
    const away = teamById(state, awayId);
    const isUser = series.teams.includes(state.clubId);
    const result = (isUser && opts.userResult && opts.live?.serieId === series.id)
      ? opts.userResult
      : simulateGame({
        home,
        away,
        seed: `${state.seed}|s${state.season}|po|${series.id}|g${gameNumber}`,
        playByPlay: isUser,
        rules: config.rules,
      });
    accumulateGame(state.seasonStats, result);
    const homeWon = result.home.score > result.away.score;
    const winnerId = homeWon ? homeId : awayId;
    series.wins[series.teams.indexOf(winnerId)] += 1;
    series.games.push({ home: homeId, away: awayId, hs: result.home.score, as: result.away.score });

    if (isUser) {
      state.boxscores.unshift(compactBox(result, gameNumber, state.season, series.id));
      state.boxscores.length = Math.min(state.boxscores.length, KEEP_BOXSCORES);
      const us = homeId === state.clubId ? result.home : result.away;
      const them = homeId === state.clubId ? result.away : result.home;
      const idx = series.teams.indexOf(state.clubId);
      events.push({
        kind: us.score > them.score ? 'win' : 'loss',
        text: `Jogo ${gameNumber}: ${us.score > them.score ? 'vitoria' : 'derrota'} ${us.score} a ${them.score}.`
          + ` Serie ${series.wins[idx]} a ${series.wins[1 - idx]} (melhor de ${needed * 2 - 1}).`,
        result,
      });
    }

    if (series.wins[0] === needed || series.wins[1] === needed) {
      series.done = true;
      series.winner = series.teams[series.wins[0] === needed ? 0 : 1];
      if (isUser) {
        events.push({
          kind: series.winner === state.clubId ? 'win' : 'loss',
          text: series.winner === state.clubId
            ? `Serie vencida por ${Math.max(...series.wins)} a ${Math.min(...series.wins)}.`
            : `Eliminado por ${Math.max(...series.wins)} a ${Math.min(...series.wins)}.`,
        });
      }
    }
  }

  advanceBracket(state.bracket, state.standings, config);

  if (state.bracket.champion) {
    const champName = teamName(state.teams, state.bracket.champion);
    state.phase = 'offseason';
    events.push({
      kind: state.bracket.champion === state.clubId ? 'title' : 'info',
      text: state.bracket.champion === state.clubId
        ? `CAMPEAO! O ${champName} e o campeao da temporada ${state.season}.`
        : `Fim da temporada. Campeao: ${champName}.`,
    });
  }
  return { state, events };
}

function compactBox(result, round, season, seriesId = null) {
  return {
    season,
    round,
    seriesId,
    home: { teamId: result.home.teamId, name: result.home.name, score: result.home.score, quarters: result.home.quarters, lines: result.home.lines, totals: result.home.totals },
    away: { teamId: result.away.teamId, name: result.away.name, score: result.away.score, quarters: result.away.quarters, lines: result.away.lines, totals: result.away.totals },
    log: result.log ? result.log.slice(-14) : null,
    overtimes: result.overtimes,
  };
}

// ------------------------------------------------- partida conduzida ao vivo

/**
 * Abre a partida do usuario SEM simular.
 *
 * O resto da rodada continua sendo simulado de uma vez -- sao 14 jogos que
 * ninguem assiste. So o jogo do usuario vira uma partida retomavel, porque so
 * nele ele pode substituir no meio.
 */
export function startUserMatch(state) {
  if (state.unemployed) return null;
  const config = configOf(state);

  if (state.phase === 'regular') {
    const fixture = userFixture(state);
    if (!fixture) return null;
    const home = teamById(state, fixture.home);
    const away = teamById(state, fixture.away);
    return {
      match: createMatch({
        home,
        away,
        seed: `${state.seed}|s${state.season}|r${state.round}|${home.id}`,
        playByPlay: true,
        rules: config.rules,
      }),
      homeId: home.id,
      awayId: away.id,
      context: 'regular',
    };
  }

  if (state.phase === 'playoffs') {
    const serie = state.bracket.series.find(
      (s) => s.round === state.bracket.round && !s.done && s.teams.includes(state.clubId),
    );
    if (!serie) return null;
    const gameNumber = serie.games.length + 1;
    const needed = seriesWinsNeeded(config, state.bracket.round);
    const homeId = seriesHomeTeam(serie, gameNumber, needed);
    const awayId = serie.teams.find((id) => id !== homeId);
    return {
      match: createMatch({
        home: teamById(state, homeId),
        away: teamById(state, awayId),
        seed: `${state.seed}|s${state.season}|po|${serie.id}|g${gameNumber}`,
        playByPlay: true,
        rules: config.rules,
      }),
      homeId,
      awayId,
      serieId: serie.id,
      context: 'playoffs',
    };
  }
  return null;
}

/**
 * Fecha a partida conduzida e faz o resto da rodada acontecer.
 * O resultado que entra na tabela e o da partida que o usuario acabou de
 * jogar -- inclusive com as substituicoes que ele fez.
 */
export function finishUserMatch(state, live) {
  const result = matchResult(live.match);
  return advanceRound(state, { userResult: result, live });
}

// ------------------------------------------------------ escalacao e tatica

/**
 * A escalacao do usuario. Guardada por ID -- e o que impede o time de congelar
 * quando alguem sai do elenco.
 *
 * `starters` sao os 5 que comecam; `minutes` e a minutagem-alvo. O motor usa a
 * minutagem como POLITICA de rotacao: nao adianta travar os 5 titulares em
 * quadra o jogo inteiro, isso nao e escalacao, e congelamento.
 */
export function setLineup(state, { starters, minutes }) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const team = userTeam(state);
  const validos = new Set(team.roster.map((p) => p.id));

  const events = [];
  team.lineup = team.lineup || {};
  if (Array.isArray(starters)) {
    const limpos = starters.filter((id) => validos.has(id));
    if (limpos.length !== 5) {
      return { state, events: [{ kind: 'error', text: 'O quinteto precisa de 5 atletas do elenco.' }] };
    }
    team.lineup.starters = limpos;
  }
  if (minutes) {
    const limpos = {};
    for (const [id, min] of Object.entries(minutes)) {
      if (validos.has(id)) limpos[id] = Math.max(0, Math.min(48, Number(min) || 0));
    }
    team.lineup.minutes = limpos;
  }
  applyMinutes(team.roster, team.lineup);
  events.push({ kind: 'info', text: 'Escalacao salva.' });
  return { state, events };
}

/** Volta para a rotacao automatica. */
export function autoLineup(state) {
  const team = userTeam(state);
  team.lineup = null;
  applyMinutes(team.roster, null);
  return { state, events: [{ kind: 'info', text: 'Rotacao automatica ligada.' }] };
}

/** Sistema de jogo. */
export function setTactics(state, tactics) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const team = userTeam(state);
  team.tactics = { ...DEFAULT_TACTICS, ...(team.tactics || {}), ...tactics };
  return { state, events: [{ kind: 'info', text: 'Sistema de jogo atualizado.' }] };
}

/** O que a tela de escalacao precisa mostrar. */
export function lineupInfo(state) {
  const team = userTeam(state);
  const auto = !team.lineup;
  const sugerido = suggestedMinutes(team.roster);
  const starters = team.lineup?.starters
    || [...team.roster].sort((a, b) => b.overall - a.overall).slice(0, 5).map((p) => p.id);
  const minutes = team.lineup?.minutes || sugerido;
  return { auto, starters, minutes, sugerido, tactics: { ...DEFAULT_TACTICS, ...(team.tactics || {}) } };
}

// ------------------------------------------------- mercado: acoes do usuario

/**
 * Sem clube, nao ha o que negociar. Uma guarda so, usada por todas as acoes --
 * espalhar a checagem por cada uma delas e como um caminho fica de fora.
 */
function semClube(state) {
  if (!state.unemployed) return null;
  return {
    state,
    events: [{ kind: 'error', text: 'Voce esta sem clube. Aceite uma vaga antes de negociar.' }],
  };
}

export function listPlayer(state, playerId) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  if (!state.listed.includes(playerId)) state.listed.push(playerId);
  return { state, events: [{ kind: 'info', text: 'Atleta posto na lista de transferencias.' }] };
}

export function unlistPlayer(state, playerId) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  state.listed = state.listed.filter((id) => id !== playerId);
  return { state, events: [{ kind: 'info', text: 'Atleta retirado da lista.' }] };
}

/** Cotacao de compra de um atleta de outro clube. */
export function quoteFor(state, playerId, offeredWage) {
  const seller = state.teams.find((t) => t.roster.some((p) => p.id === playerId));
  const player = seller?.roster.find((p) => p.id === playerId);
  if (!player) return null;
  const buyer = userTeam(state);
  const q = quote({
    player, buyer, seller, standings: state.standings, offeredWage, econ: economyOf(state),
  });
  return { player, seller, quote: q };
}

/**
 * Assina um patrocínio.
 *
 * Assinar é definitivo pelo prazo do contrato: não há rescisão. Deixar sair no
 * meio tiraria o peso da escolha, que é justamente o ponto -- apostar no bônus
 * e não entregar tem de doer.
 */
export function signSponsor(state, offerId) {
  const oferta = state.sponsorOffers.find((o) => o.id === offerId);
  if (!oferta) return { state, events: [{ kind: 'error', text: 'Essa proposta não está mais na mesa.' }] };
  if (state.sponsor) {
    return { state, events: [{ kind: 'error', text: `Você já tem contrato com ${state.sponsor.marca}.` }] };
  }
  state.sponsor = { ...oferta, anosRestantes: oferta.anos, assinadoNaTemporada: state.season };
  state.sponsorOffers = [];
  return {
    state,
    events: [{
      kind: 'win',
      text: `${oferta.marca} é o novo patrocinador: ${oferta.fixo.toFixed(1)} mi por temporada, `
        + `mais ${oferta.bonus.toFixed(1)} mi se ${oferta.metaLabel}.`,
    }],
  };
}

/** O usuario faz uma proposta de compra. */
export function bidForPlayer(state, { playerId, fee, wage }) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const found = quoteFor(state, playerId, wage);
  if (!found) return { state, events: [{ kind: 'error', text: 'Atleta nao encontrado.' }] };
  const { player, seller, quote: q } = found;
  const buyer = userTeam(state);

  if (state.phase !== 'regular' && state.phase !== 'offseason') {
    return { state, events: [{ kind: 'error', text: 'O mercado esta fechado durante os playoffs.' }] };
  }
  if (state.phase === 'regular' && state.round > deadlineOf(state)) {
    return { state, events: [{ kind: 'error', text: 'A data limite de trocas ja passou.' }] };
  }

  const blocked = blockers({ player, buyer, fee, wage, quote: q, econ: economyOf(state) });
  if (blocked.length) return { state, events: blocked.map((text) => ({ kind: 'error', text })) };

  const verdict = evaluateBid({ player, fee, wage, q });
  if (verdict.status !== 'accepted') {
    return { state, events: [{ kind: verdict.status === 'counter' ? 'counter' : 'error', text: verdict.message, counterFee: verdict.counterFee, counterWage: verdict.counterWage }] };
  }

  const feito = transferPlayer(state, { player, from: seller, to: buyer, fee, wage });
  if (!feito.ok) return { state, events: [{ kind: 'error', text: feito.error }] };
  return {
    state,
    events: [{ kind: 'win', text: `${player.name} e do ${buyer.name}. ${fee.toFixed(1)} mi para o ${seller.name}.` }],
  };
}

/** Aceita uma proposta recebida por um atleta seu. */
export function acceptOffer(state, offerId) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const offer = state.offers.find((o) => o.id === offerId);
  if (!offer) return { state, events: [{ kind: 'error', text: 'Proposta nao existe mais.' }] };
  const seller = userTeam(state);
  const player = seller.roster.find((p) => p.id === offer.playerId);
  const buyer = teamById(state, offer.buyerId);
  if (!player) return { state, events: [{ kind: 'error', text: 'Esse atleta nao esta mais no elenco.' }] };

  const econ = economyOf(state);
  if (seller.roster.length <= econ.minRoster) {
    return {
      state,
      events: [{ kind: 'error', text: `Nao da: o elenco ficaria com ${seller.roster.length - 1} atletas e o minimo e ${econ.minRoster}.` }],
    };
  }

  const feito = transferPlayer(state, { player, from: seller, to: buyer, fee: offer.fee, wage: offer.wage });
  if (!feito.ok) {
    // O comprador encheu o elenco entre a proposta e o aceite: a proposta cai.
    state.offers = state.offers.filter((o) => o.id !== offerId);
    return { state, events: [{ kind: 'error', text: `${feito.error} A proposta caiu.` }] };
  }
  state.offers = state.offers.filter((o) => o.id !== offerId && o.playerId !== offer.playerId);
  state.listed = state.listed.filter((id) => id !== offer.playerId);
  return { state, events: [{ kind: 'win', text: `${player.name} vendido ao ${buyer.name} por ${offer.fee.toFixed(1)} mi.` }] };
}

export function rejectOffer(state, offerId) {
  state.offers = state.offers.filter((o) => o.id !== offerId);
  return { state, events: [{ kind: 'info', text: 'Proposta recusada.' }] };
}

/** Contraproposta do usuario sobre uma oferta recebida. */
export function counterOffer(state, offerId, counterFee) {
  const offer = state.offers.find((o) => o.id === offerId);
  if (!offer) return { state, events: [{ kind: 'error', text: 'Proposta nao existe mais.' }] };
  const buyer = teamById(state, offer.buyerId);
  const player = userTeam(state).roster.find((p) => p.id === offer.playerId);
  if (!player) return { state, events: [{ kind: 'error', text: 'Atleta nao esta mais no elenco.' }] };

  const verdict = aiAcceptsCounter({ buyer, player, originalFee: offer.fee, counterFee });
  if (!verdict.ok) {
    state.offers = state.offers.filter((o) => o.id !== offerId);
    return { state, events: [{ kind: 'error', text: verdict.message }] };
  }
  offer.fee = counterFee;
  offer.expiresRound = state.round + 2;
  return { state, events: [{ kind: 'counter', text: verdict.message, offerId }] };
}

/** Dispensa: custa dinheiro e nao devolve nada. */
export function releasePlayer(state, playerId) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const team = userTeam(state);
  const player = team.roster.find((p) => p.id === playerId);
  if (!player) return { state, events: [{ kind: 'error', text: 'Atleta nao encontrado.' }] };
  const econ = economyOf(state);
  if (team.roster.length <= econ.minRoster) {
    return { state, events: [{ kind: 'error', text: `Minimo de ${econ.minRoster} atletas no elenco.` }] };
  }
  const cost = releaseCost(player);
  if (cost > team.money) {
    return { state, events: [{ kind: 'error', text: `A rescisao custa ${cost.toFixed(1)} mi e voce tem ${team.money.toFixed(1)}.` }] };
  }
  team.money = Math.round((team.money - cost) * 10) / 10;
  team.roster = team.roster.filter((p) => p.id !== playerId);
  state.listed = state.listed.filter((id) => id !== playerId);
  state.offers = state.offers.filter((o) => o.playerId !== playerId);
  return { state, events: [{ kind: 'info', text: `${player.name} dispensado. Rescisao de ${cost.toFixed(1)} mi.` }] };
}

/**
 * A unica porta por onde um atleta troca de clube.
 *
 * A checagem de teto mora AQUI, e nao em cada chamador: uma proposta criada
 * quando o comprador tinha vaga podia ser aceita depois, ja com o elenco cheio
 * -- e o time terminava com 16. Espalhar a regra pelos chamadores e como um
 * caminho fica de fora.
 *
 * @returns {{ok:boolean, error?:string}}
 */
function transferPlayer(state, { player, from, to, fee, wage }) {
  const econ = economyOf(state);
  if (to.roster.length >= econ.maxRoster) {
    return { ok: false, error: `O elenco do ${to.name} esta cheio (${econ.maxRoster}).` };
  }
  if (from.roster.length <= econ.minRoster) {
    return { ok: false, error: `O ${from.name} nao pode ficar com menos de ${econ.minRoster} atletas.` };
  }
  from.roster = from.roster.filter((p) => p.id !== player.id);
  player.wage = wage ?? player.wage;
  player.contractYears = Math.max(2, player.contractYears || 2);
  to.roster.push(player);
  from.money = Math.round((from.money + fee) * 10) / 10;
  to.money = Math.round((to.money - fee) * 10) / 10;
  state.news.unshift({
    round: state.round,
    season: state.season,
    kind: 'transfer',
    text: `${player.name} (${player.overall}) deixou o ${from.name} rumo ao ${to.name} por ${fee.toFixed(1)} mi.`,
  });
  state.news.length = Math.min(state.news.length, 40);
  return { ok: true };
}

/** A IA tambem negocia entre si -- o mercado nao pode girar so em volta do usuario. */
/**
 * O mercado entre os OUTROS clubes, que roda sozinho a cada rodada.
 *
 * ⚠️ A versao anterior nunca fechava um negocio -- medido em 04/09/2026: ZERO
 * transferencias numa temporada inteira, nas duas ligas. A causa era uma
 * contradicao entre duas regras que pareciam certas isoladamente:
 *
 *   o vendedor so oferecia quem estava FORA da rotacao de 9 (uma sobra)
 *   o comprador so aceitava quem MELHORASSE o oitavo melhor dele
 *
 * Ou seja: so se vendia quem ninguem podia querer. E falhava em silencio, sem
 * erro nenhum -- o mercado simplesmente parecia congelado.
 *
 * Agora sao tres tentativas por rodada, e cada clube age pelo que lhe falta:
 * elenco curto compra qualquer reforco util, elenco cheio se desfaz de quem
 * nao joga, e caixa baixo obriga a vender.
 */
function aiTransferRound(state, rng, events, econ) {
  const outros = state.teams.filter((t) => t.id !== state.clubId);
  if (outros.length < 2) return;

  // Tres tentativas: uma so por rodada faz o mercado depender de sorte demais.
  for (let tentativa = 0; tentativa < 3; tentativa++) {
    if (!rng.chance(0.30)) continue;

    const seller = outros[rng.int(outros.length)];
    if (seller.roster.length <= econ.minRoster) continue;

    const ordenado = [...seller.roster].sort((a, b) => b.overall - a.overall);
    const apertado = seller.money < econ.salaryCap * 0.12;

    /* Quem entra na vitrine: sempre as sobras, e -- se o caixa apertou --
       tambem alguem da rotacao. Um clube sem dinheiro vende quem da dinheiro,
       nao quem sobra. */
    const vitrine = apertado ? ordenado.slice(5) : ordenado.slice(8);
    if (!vitrine.length) continue;
    const player = vitrine[rng.int(vitrine.length)];

    const buyers = outros.filter((t) => t.id !== seller.id && aiQuerReforco(t, player, econ));
    if (!buyers.length) continue;
    const buyer = buyers[rng.int(buyers.length)];

    const max = aiMaxFee(buyer, player);
    const value = player.marketValue ?? 1;
    // Clube apertado aceita menos: e disso que se trata vender por necessidade.
    const piso = apertado ? value * 0.8 : value;
    if (max < piso) continue;

    const fee = Math.round(Math.min(max, value * rng.range(0.95, 1.15)) * 10) / 10;
    const feito = transferPlayer(state, { player, from: seller, to: buyer, fee, wage: wageOf(player) });
    if (!feito.ok) continue;

    events.push({
      kind: 'info',
      text: `Mercado: ${player.name} saiu do ${seller.name} para o ${buyer.name} por ${fee.toFixed(1)} mi.`,
    });
  }
}

/**
 * A IA aceita este atleta como REFORCO, e nao so como titular.
 *
 * `aiWants` (usado nas propostas ao usuario) exige melhorar o oitavo melhor --
 * o criterio certo para gastar dinheiro num alvo. Entre clubes da IA, porem, a
 * maior parte do mercado real e de banco: quem esta com elenco curto pega quem
 * apenas ajuda.
 */
function aiQuerReforco(buyer, player, econ) {
  if (buyer.roster.length >= econ.maxRoster) return false;
  const folha = payrollOf(buyer.roster);
  if (folha + (player.wage || wageOf(player)) > econ.salaryCap * 1.1) return false;

  const ordenado = [...buyer.roster].sort((a, b) => b.overall - a.overall);
  const curto = buyer.roster.length <= econ.minRoster + 2;

  // Elenco curto: basta ser melhor que o pior do grupo.
  if (curto) return player.overall > (ordenado[ordenado.length - 1]?.overall ?? 0);

  // Elenco normal: tem de melhorar a rotacao de verdade, ou ser aposta jovem.
  const nono = ordenado[8]?.overall ?? 60;
  const teto = player.potential ?? player.overall;
  return player.overall > nono || (player.age <= 23 && teto > nono + 3);
}

// ------------------------------------------------------------- entressafra

/**
 * Fecha a temporada e abre o DRAFT.
 *
 * A ordem importa e e a da vida real: primeiro a temporada fecha (imposto de
 * luxo, receita, idade, aposentadoria), depois o draft repoe as vagas, e so
 * entao a proxima temporada comeca. Fazer o draft antes das aposentadorias
 * encheria elencos que ainda iam perder gente.
 */
export function beginDraft(state) {
  const events = [];
  const config = configOf(state);
  const econ = economyOf(state);
  const champion = state.bracket?.champion;

  // Premios da temporada, calculados uma vez, no fim.
  state.awards = computeAwards({
    seasonStats: state.seasonStats,
    standings: state.standings,
    teams: state.teams,
    totalRounds: config.regularRounds,
  });

  if (!state.unemployed) {
    const registro = closeSeasonRecord(state, champion);
    registro.awards = state.awards;
    state.history.push(registro);

    // A diretoria decide se o tecnico fica.
    const target = seasonTarget(userTeam(state), state.teams);
    const madePlayoffs = !!state.bracket?.series.some(
      (x) => x.round === 1 && x.teams.includes(state.clubId),
    );
    state.reputation = updateReputation(state.reputation, {
      trophy: registro.trophy, record: registro.userRecord, target, fired: false,
    });
    if (shouldFireEndOfSeason({
      record: registro.userRecord, target, madePlayoffs,
      seed: state.seed, season: state.season,
    })) {
      registro.fired = true;
      state.reputation = Math.max(1, state.reputation - 12);
      state.formerClubId = state.clubId;
      state.unemployed = true;
      events.push({
        kind: 'error',
        text: `O ${userTeam(state).name} decidiu nao renovar. Voce esta sem clube.`,
      });
    }
  } else if (state.awards) {
    state.history.push({
      season: state.season, leagueId: state.leagueId,
      champion, championName: champion ? teamName(state.teams, champion) : null,
      userRecord: null, trophy: null, unemployedSeason: true, awards: state.awards,
    });
  }

  for (const team of state.teams) {
    const tax = luxuryTaxOf(team.roster, econ);
    if (tax > 0) {
      team.money = Math.round((team.money - tax) * 10) / 10;
      if (team.id === state.clubId) {
        events.push({ kind: 'error', text: `Imposto de luxo: ${tax.toFixed(1)} mi. Sua folha passou do teto de ${econ.salaryCap}.` });
      }
    }
    const row = state.standings[team.id];
    const winPct = row && row.games ? row.wins / row.games : 0.5;
    const revenue = Math.round((econ.revenueBase + winPct * econ.revenueWin) * 10) / 10;
    team.money = Math.round((team.money + revenue) * 10) / 10;
    if (team.id === state.clubId) {
      events.push({ kind: 'info', text: `Receita da temporada: ${revenue.toFixed(1)} mi.` });

      /* O patrocínio é pago DEPOIS da receita e do imposto, e só para o clube
         do usuário: é a única equipe que assina contrato por decisão de
         alguém. Os outros já têm a receita deles. */
      if (state.sponsor) {
        const row2 = state.standings[team.id];
        const pago = pagamentoDoPatrocinio(state.sponsor, {
          vitorias: row2?.wins ?? 0,
          derrotas: row2?.losses ?? 0,
          chegouPlayoffs: !!state.bracket?.series.some(
            (x) => x.round === 1 && x.teams.includes(state.clubId),
          ),
          chegouFinal: !!state.bracket?.series.some(
            (x) => x.round === (state.bracket?.rounds ?? 99) && x.teams.includes(state.clubId),
          ),
          campeao: state.bracket?.champion === state.clubId,
        });
        team.money = Math.round((team.money + pago.total) * 10) / 10;
        events.push({ kind: pago.bateu ? 'win' : 'info', text: pago.texto });

        state.sponsor.anosRestantes = (state.sponsor.anosRestantes ?? state.sponsor.anos) - 1;
        if (state.sponsor.anosRestantes <= 0) {
          events.push({ kind: 'info', text: `O contrato com ${state.sponsor.marca} chegou ao fim.` });
          state.sponsor = null;
        }
      }
    }

    for (const player of team.roster) {
      player.age += 1;
      player.contractYears = Math.max(0, (player.contractYears || 1) - 1);
      player.rookie = false;
      const rng = makeRng(`evolucao|${state.seed}|${state.season}|${player.id}`);
      const room = (player.potential ?? player.overall) - player.overall;
      let delta = 0;
      if (player.age <= 24) delta = Math.round(rng.range(0, Math.max(1, room * 0.5)));
      else if (player.age <= 29) delta = Math.round(rng.range(-1, 1.4));
      else if (player.age <= 33) delta = -Math.round(rng.range(0, 2.2));
      else delta = -Math.round(rng.range(1, 3.4));
      player.overall = Math.max(40, Math.min(99, player.overall + delta));
      player.wage = 0; // recalculado na escala da liga
      enrichPlayer(player, { season: state.season + 1, seed: state.seed, scale: econ.moneyScale });
    }

    const retired = team.roster.filter((p) => p.age >= 38 || (p.age >= 36 && p.overall < 70));
    team.roster = team.roster.filter((p) => !retired.includes(p));

    // Repoe o piso AQUI, logo depois das aposentadorias. Deixar para o comeco
    // da temporada abria uma janela -- entre o draft e a primeira rodada -- em
    // que um elenco podia estar abaixo do minimo. O draft entra depois disso, e
    // o que ele traz e ganho, nao remendo.
    while (team.roster.length < econ.minRoster) {
      team.roster.push(makeReplacement(state, team));
    }
    if (team.id === state.clubId && retired.length) {
      events.push({
        kind: 'info',
        text: `${retired.length} atleta(s) se aposentaram: ${retired.map((p) => p.name).join(', ')}.`,
      });
    }
  }

  const rng = makeRng(`draft|${state.seed}|${state.season}`);
  state.draft = buildDraft({ state, config, seed: state.seed, rng });
  state.phase = 'draft';

  // Vagas para quem esta sem clube: e na entressafra que o mercado de
  // treinadores se move.
  if (state.unemployed) {
    state.jobOffers = jobOffers({
      teams: state.teams, standings: state.standings, currentClubId: null,
      reputation: state.reputation ?? JOB.startingReputation,
      seed: state.seed, season: state.season,
    });
    events.push({
      kind: 'offer',
      text: `${state.jobOffers.length} clube(s) procuram tecnico. Veja em Emprego.`,
    });
  }

  const myPick = state.draft.order.indexOf(state.clubId) + 1;
  if (state.draft.lottery) {
    const won = state.draft.lottery.winners[0];
    events.push({
      kind: won === state.clubId ? 'title' : 'info',
      text: won === state.clubId
        ? 'A LOTERIA FOI SUA! Voce escolhe em primeiro no draft.'
        : `Loteria do draft: a primeira escolha ficou com o ${teamName(state.teams, won)}.`,
    });
  }
  events.push({ kind: 'info', text: `Sua primeira escolha e a de numero ${myPick}.` });
  return { state, events };
}

/**
 * O registro de uma temporada: quem foi campeao, como foi a sua campanha, e
 * qual TROFEU voce levou. E daqui que sai a galeria -- por isso o titulo e
 * gravado na virada, e nao recalculado depois: refazer a conta anos mais tarde,
 * com o elenco ja trocado, e como o historico se perde.
 */
function closeSeasonRecord(state, champion) {
  const row = { ...state.standings[state.clubId] };
  const all = sortedStandings(state.standings);
  const bestRecord = all[0];
  const bracket = state.bracket;

  /* ⚠️ TODAS as conquistas da temporada, e nao so a maior.
     A versao anterior guardava um troteu unico, com prioridade: quem era
     campeao nunca registrava "chegou a final", "entrou nos playoffs" nem
     "melhor campanha". Medido em 04/09/2026, em 24 temporadas jogadas:
     melhor campanha conquistada 13 vezes e registrada ZERO. A Sala de Trofeus
     mostrava vazio o que a pessoa tinha ganho. */
  const trophies = [];
  if (bracket) {
    const ultimaRodada = Math.max(...bracket.series.map((x) => x.round));
    const jogouPlayoffs = bracket.series.some((s) => s.round === 1 && s.teams.includes(state.clubId));
    const chegouNaFinal = bracket.series.some(
      (s) => s.round === ultimaRodada && s.teams.includes(state.clubId),
    );
    if (jogouPlayoffs) trophies.push('playoffs');
    if (chegouNaFinal) trophies.push('conferencia');
  }
  if (bestRecord?.teamId === state.clubId) trophies.push('campanha');
  if (champion === state.clubId) trophies.push('liga');

  /* `trophy` continua sendo a MAIOR conquista, sozinha: a reputacao e a
     decisao da diretoria olham para ela, e um save antigo tambem so tem esse
     campo. A lista e um acrescimo, nao uma troca. */
  const ordem = ['liga', 'conferencia', 'campanha', 'playoffs'];
  const trophy = ordem.find((t) => trophies.includes(t)) || null;

  return {
    season: state.season,
    leagueId: state.leagueId,
    champion,
    championName: champion ? teamName(state.teams, champion) : null,
    userRecord: row,
    userTeamName: userTeam(state).name,
    trophy,
    trophies,
    bestRecordTeam: bestRecord?.name || null,
    bestRecordWins: bestRecord?.wins ?? null,
    leader: topScorerOf(state),
  };
}

/** O cestinha da SUA equipe na temporada -- vira linha do historico. */
function topScorerOf(state) {
  let best = null;
  for (const line of Object.values(state.seasonStats)) {
    if (line.teamId !== state.clubId || !line.games) continue;
    const avg = line.pts / line.games;
    if (!best || avg > best.avg) best = { name: line.name, avg: Math.round(avg * 10) / 10 };
  }
  return best;
}

/** Uma escolha: entrega o calouro ao clube e anda a fila. */
function applyPick(state, teamId, prospect) {
  const draft = state.draft;
  const info = currentPick(draft);
  const team = teamById(state, teamId);
  prospect.draftedBy = teamId;

  // O calouro entra no elenco com VALOR e SALARIO na escala da liga. Sem isto
  // ele entrava valendo `undefined`: a folha salarial do clube passava a mentir,
  // e ninguem conseguia negocia-lo. Foram 1413 avisos no teste de 10
  // temporadas, todos desta linha que faltava.
  const econ = economyOf(state);
  const potencialReal = prospect.potential;
  enrichPlayer(prospect, {
    season: state.season + 1,
    seed: `${state.seed}|calouro`,
    scale: econ.moneyScale,
  });
  // enrichPlayer sorteia um potencial novo; o do draft e o que vale.
  prospect.potential = Math.max(potencialReal, prospect.overall);
  prospect.contractYears = 3;
  prospect.rookie = true;

  team.roster.push(prospect);

  // O draft nao pode estourar o teto de elenco. Na vida real o calouro toma a
  // vaga de alguem -- aqui sai o pior do elenco, e nunca o proprio calouro.
  // Quem acabou de ser escolhido NESTE draft esta protegido -- senao o time
  // corta na segunda rodada o calouro que levou na primeira.
  const escolhidosAgora = new Set(draft.picks.filter((x) => x.teamId === teamId).map((x) => x.playerId));
  escolhidosAgora.add(prospect.id);
  while (team.roster.length > econ.maxRoster) {
    const pior = team.roster
      .filter((p) => !escolhidosAgora.has(p.id))
      .sort((a, b) => a.overall - b.overall)[0];
    if (!pior) break;
    team.roster = team.roster.filter((p) => p.id !== pior.id);
    if (teamId === state.clubId) {
      state.news.unshift({
        season: state.season,
        kind: 'release',
        text: `${pior.name} foi dispensado para abrir vaga para ${prospect.name}.`,
      });
    }
  }
  draft.picks.push({
    round: info.round,
    pick: info.pick,
    overall: info.overall,
    teamId,
    teamName: team.name,
    playerId: prospect.id,
    playerName: prospect.name,
    position: prospect.position,
  });
  draft.current += 1;
  if (draft.current >= draft.order.length * draft.rounds) draft.done = true;
}

/**
 * Roda o draft ate a vez do usuario (ou ate o fim, se ele nao tiver mais
 * escolhas).
 */
export function runDraftUntilUser(state) {
  const events = [];
  const draft = state.draft;
  if (!draft || draft.done) return { state, events };
  const rng = makeRng(`draftia|${state.seed}|${draft.season}|${draft.current}`);

  let guard = 0;
  while (!draft.done && guard++ < 500) {
    const info = currentPick(draft);
    if (info.teamId === state.clubId) break;
    const team = teamById(state, info.teamId);
    const choice = aiChoice(draft, team, rng);
    if (!choice) { draft.done = true; break; }
    applyPick(state, info.teamId, choice);
    if (info.overall <= 5) {
      events.push({
        kind: 'info',
        text: `${info.overall}a escolha: ${team.name} leva ${choice.name} (${choice.position}).`,
      });
    }
  }
  if (draft.done) events.push({ kind: 'info', text: 'Draft encerrado.' });
  return { state, events };
}

/** O usuario escolhe um calouro. */
export function draftPlayer(state, prospectId) {
  const bloqueio = semClube(state);
  if (bloqueio) return bloqueio;
  const draft = state.draft;
  if (!draft || draft.done) return { state, events: [{ kind: 'error', text: 'O draft ja acabou.' }] };
  const info = currentPick(draft);
  if (info.teamId !== state.clubId) {
    return { state, events: [{ kind: 'error', text: 'Nao e a sua vez de escolher.' }] };
  }
  const prospect = draft.prospects.find((p) => p.id === prospectId && !p.draftedBy);
  if (!prospect) return { state, events: [{ kind: 'error', text: 'Esse calouro ja foi escolhido.' }] };

  const econ = economyOf(state);
  const team = userTeam(state);
  if (team.roster.length >= econ.maxRoster) {
    return {
      state,
      events: [{ kind: 'error', text: `Elenco cheio (${econ.maxRoster}). Dispense alguem antes de usar a escolha.` }],
    };
  }

  applyPick(state, state.clubId, prospect);
  const events = [{
    kind: 'win',
    text: `${info.overall}a escolha: voce leva ${prospect.name}, ${prospect.position}, ${prospect.age} anos.`,
  }];
  const after = runDraftUntilUser(state);
  return { state, events: [...events, ...after.events] };
}

/** Passa a vez: leva o melhor do board mesmo assim. */
export function autoDraftPick(state) {
  const draft = state.draft;
  const info = currentPick(draft);
  if (!info || info.teamId !== state.clubId) return { state, events: [] };
  const best = draft.prospects.find((p) => !p.draftedBy);
  if (!best) return { state, events: [] };
  return draftPlayer(state, best.id);
}

/** Fecha o draft e monta a proxima temporada. */
export function startNextSeason(state) {
  const events = [];
  const config = configOf(state);
  const econ = economyOf(state);

  for (const team of state.teams) {
    while (team.roster.length < econ.minRoster + 1) {
      team.roster.push(makeReplacement(state, team));
    }
    while (team.roster.length > econ.maxRoster) {
      // Elenco acima do teto nao entra em temporada: corta o pior.
      const worst = [...team.roster].sort((a, b) => a.overall - b.overall)[0];
      team.roster = team.roster.filter((p) => p.id !== worst.id);
      if (team.id === state.clubId) {
        events.push({ kind: 'info', text: `${worst.name} foi dispensado para o elenco caber no limite.` });
      }
    }
  }

  /* As propostas de patrocínio chegam com a temporada nova, e só quando não
     há contrato em vigor -- ninguém oferece a quem já assinou com outro. O
     tamanho delas depende do que o clube fez e do elenco que tem: uma marca
     paga pelo que o time vale, não por simpatia. */
  if (!state.unemployed && !state.sponsor) {
    const meu = userTeam(state);
    const forcaDe = (t) => {
      const top8 = [...t.roster].sort((a, b) => b.overall - a.overall).slice(0, 8);
      return top8.reduce((a, p) => a + p.overall, 0) / Math.max(1, top8.length);
    };
    const medias = state.teams.map(forcaDe);
    const minha = forcaDe(meu);
    const pior = Math.min(...medias); const melhor = Math.max(...medias);
    const forca = melhor > pior ? (minha - pior) / (melhor - pior) : 0.5;
    const anterior = state.history[state.history.length - 1]?.userRecord;
    const jogos = (anterior?.wins ?? 0) + (anterior?.losses ?? 0);
    const aproveitamento = jogos ? (anterior.wins / jogos) : 0.5;

    state.sponsorOffers = makeSponsorOffers({
      econ, seed: state.seed, season: state.season + 1, forca, aproveitamento,
    });
    events.push({
      kind: 'offer',
      text: `${state.sponsorOffers.length} propostas de patrocínio na mesa. Veja no painel.`,
    });
  }

  state.season += 1;
  state.round = 1;
  state.phase = 'regular';
  state.draft = null;
  state.schedule = buildSchedule(state.teams, `${state.seed}|${state.season}`, config.regularRounds);
  state.standings = emptyStandings(state.teams);
  state.seasonStats = {};
  state.results = [];
  state.boxscores = [];
  state.bracket = null;
  state.offers = [];
  events.push({ kind: 'info', text: `Temporada ${state.season} comecando.` });
  return { state, events };
}

function makeReplacement(state, team) {
  const rng = makeRng(`reposicao|${state.seed}|${state.season}|${team.id}|${team.roster.length}`);
  const base = team.roster.length
    ? team.roster.reduce((a, p) => a + p.overall, 0) / team.roster.length
    : 70;
  // Usa a MESMA fabrica de atletas do resto do jogo. Criar um objeto "parecido
  // com atleta" aqui deixaria um jogador sem atributos dentro do elenco, e o
  // motor de posse quebraria so quando ele entrasse em quadra.
  const player = makePlayer({
    position: POSITIONS[rng.int(POSITIONS.length)],
    talent: base - 75 - 10 + rng.range(0, 7),
    nationality: 'BR',
    age: 19 + rng.int(3),
    seed: `reposicao|${state.seed}|${state.season}|${team.id}|${team.roster.length}|${rng()}`,
    takenNames: new Set(),
  });
  enrichPlayer(player, { season: state.season, seed: state.seed, scale: economyOf(state).moneyScale });
  assignTargetMinutes(team.roster.concat(player));
  return player;
}

// --------------------------------------------------------------- save/load

export function serializeCareer(state) {
  return JSON.stringify(state);
}

/**
 * A chave do DESAFIO DA SEMANA -- separada da carreira, e isso nao e detalhe.
 *
 * ⚠️ O desafio e uma carreira de 10 rodadas com as mesmas funcoes do motor. Se
 * ele gravasse na mesma chave, entrar no desafio APAGARIA a carreira de quem
 * joga ha dez temporadas -- e para quem nao e premium nao existe copia na
 * nuvem para voltar atras.
 */
export const SAVE_KEY_DESAFIO = 'basquete82.desafio.v1';

export function saveCareer(state, chave = SAVE_KEY) {
  try {
    const json = serializeCareer(state);
    localStorage.setItem(chave, json);
    return { ok: true, bytes: json.length };
  } catch (err) {
    // Cota estourada e o cenario que mata um save. Falhar em silencio aqui e
    // o pior resultado possivel -- devolve o erro para a tela avisar.
    return { ok: false, error: String(err?.message || err) };
  }
}

export function loadCareer(chave = SAVE_KEY) {
  try {
    const raw = localStorage.getItem(chave);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (state.version !== 1) return null;
    return state;
  } catch {
    return null;
  }
}

export function deleteCareer(chave = SAVE_KEY) {
  try { localStorage.removeItem(chave); } catch { /* nada a fazer */ }
}

// ---------------------------------------------------- emprego do tecnico

/** Meta e humor da diretoria, para a tela mostrar. */
export function jobStatus(state) {
  const team = userTeam(state);
  const target = seasonTarget(team, state.teams);
  const record = state.standings[state.clubId];
  const mood = boardMood({ record, target, round: state.round });
  return {
    target,
    targetText: targetLabel(target),
    mood,
    ...moodLabel(mood),
    reputation: state.reputation ?? JOB.startingReputation,
    reputationText: reputationLabel(state.reputation ?? JOB.startingReputation),
    record,
  };
}

/**
 * Demissao no meio da temporada. O jogo NAO acaba: a carreira continua sem
 * clube, e as propostas chegam na entressafra. E o que acontece de verdade --
 * tecnico demitido em janeiro raramente assume outro time no mesmo ano.
 */
function fireUser(state, motivo) {
  const anterior = userTeam(state).name;
  state.reputation = updateReputation(state.reputation, {
    trophy: null,
    record: state.standings[state.clubId],
    target: seasonTarget(userTeam(state), state.teams),
    fired: true,
  });
  state.history.push({
    season: state.season,
    leagueId: state.leagueId,
    userTeamName: anterior,
    userRecord: { ...state.standings[state.clubId] },
    trophy: null,
    fired: true,
    champion: null,
    championName: null,
  });
  state.formerClubId = state.clubId;
  state.unemployed = true;
  state.offers = [];
  state.listed = [];
  return {
    kind: 'error',
    text: `Voce foi demitido pelo ${anterior}. ${motivo}`,
  };
}

/** Aceita uma vaga e recomeca em outro clube. */
export function takeJob(state, teamId) {
  const team = teamById(state, teamId);
  if (!team) return { state, events: [{ kind: 'error', text: 'Esse clube nao existe.' }] };
  if (!state.unemployed) {
    return { state, events: [{ kind: 'error', text: 'Voce ja tem clube.' }] };
  }
  state.clubId = teamId;
  state.unemployed = false;
  state.jobOffers = null;
  state.boxscores = [];
  return {
    state,
    events: [{ kind: 'win', text: `Voce e o novo tecnico do ${team.name}.` }],
  };
}

/** Gera as vagas disponiveis para quem esta sem clube. */
export function refreshJobOffers(state) {
  state.jobOffers = jobOffers({
    teams: state.teams,
    standings: state.standings,
    currentClubId: state.clubId,
    reputation: state.reputation ?? JOB.startingReputation,
    seed: state.seed,
    season: state.season,
  });
  return state.jobOffers;
}

/** Resumo para a tela inicial. */
export function careerSummary(state) {
  const team = userTeam(state);
  const config = configOf(state);
  const econ = economyOf(state);
  const row = state.standings[state.clubId];
  const conf = sortedStandings(state.standings, config.conferences ? team.conference : null);
  return {
    team,
    config,
    econ,
    record: row,
    seed: conf.findIndex((r) => r.teamId === state.clubId) + 1,
    payroll: payrollOf(team.roster),
    capSpace: Math.round((econ.salaryCap - payrollOf(team.roster)) * 10) / 10,
  };
}
